import { useCallback, useEffect, useRef } from "react";
import { getNearestOverflowAncestor } from "@floating-ui/utils/dom";
import usePrevious from "@restart/hooks/usePrevious";
import useEventListener from "@restart/hooks/useEventListener";
import debounce from "lodash/debounce";
import { PORTAL_DELAY_DURATION_MS } from "../usePortal";
import { portalEventEmitter } from "./portalEventEmitter";
import { trapEvent } from "../../utils/EventHelpers";
import { getFocusableChildren, getFocusedIndex } from "../../utils/keyboard";
const isVirtualElement = (reference) => {
    return Boolean(reference && "getBoundingClientRect" in reference);
};
const isDomElement = (reference) => {
    return Boolean(reference && "matches" in reference);
};
export const getElementFromReferenceElement = (reference) => {
    if (isVirtualElement(reference)) {
        return reference.contextElement;
    }
    return reference;
};
const generateGetBoundingClientRect = (x = 0, y = 0) => () => ({
    height: 0,
    width: 0,
    top: y,
    right: x,
    bottom: y,
    left: x,
    toJSON: () => null,
    x,
    y,
});
export const useContextMenu = ({ onOpenChange, refs }, { enabled }) => {
    const virtualElement = useRef(null);
    if (!enabled) {
        return {};
    }
    refs.setPositionReference(virtualElement.current);
    return {
        reference: {
            onContextMenu: (e) => {
                var _a;
                if (((_a = document.getSelection()) === null || _a === void 0 ? void 0 : _a.toString()) !== "") {
                    return;
                }
                e.preventDefault();
                virtualElement.current = {
                    getBoundingClientRect: generateGetBoundingClientRect(e.clientX, e.clientY),
                };
                onOpenChange(true);
            },
        },
    };
};
/** Handles closing open portal on 'Esc' key when focused element is neither in target or portal */
export const useEscapeOutsideCloses = ({ open, onOpenChange, refs: { reference, floating } }, { enabled }) => {
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const handleEscape = (e) => {
            var _a, _b;
            if (e.key === "Escape" &&
                reference.current !== document.activeElement &&
                floating.current !== document.activeElement &&
                !((_a = getElementFromReferenceElement(reference.current)) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) &&
                !((_b = floating.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement)) &&
                open) {
                e.preventDefault();
                e.stopPropagation();
                onOpenChange(false);
            }
        };
        document.addEventListener("keydown", handleEscape, { capture: true });
        return () => document.removeEventListener("keydown", handleEscape, { capture: true });
    }, [reference.current, floating.current, open, onOpenChange]);
    return {};
};
export const useHidePortalWhenOutsideDisabledTarget = ({ open, refs: { reference } }, { enabled, hide }) => {
    useEffect(() => {
        var _a;
        // workaround for onPointerLeave not working in Safari for disabled controls
        const hidePortalWhenOutsideTarget = (event) => {
            if (event.x === 0 && event.y === 0) {
                // ignore spurious mousemove events
                return;
            }
            if (event.target instanceof Element && event.target.closest("[data-portal-target]")) {
                return;
            }
            hide(PORTAL_DELAY_DURATION_MS, false);
        };
        if (enabled && ((_a = getElementFromReferenceElement(reference.current)) === null || _a === void 0 ? void 0 : _a.hasAttribute("disabled")) && open) {
            document === null || document === void 0 ? void 0 : document.addEventListener("mousemove", hidePortalWhenOutsideTarget);
        }
        return () => document === null || document === void 0 ? void 0 : document.removeEventListener("mousemove", hidePortalWhenOutsideTarget);
    }, [enabled, open, reference.current]);
    return {};
};
export const useClosePortalOnScroll = ({ open, refs: { reference } }, { enabled, hide }) => {
    if (!enabled) {
        return {};
    }
    /**
     * Close portal on scroll, when triger is `hover`.
     */
    const handleScroll = useCallback(debounce(() => {
        if (enabled) {
            hide(0, false);
        }
    }, 1000, { leading: true, trailing: false }), [open]);
    const referenceElement = reference.current;
    // scroll event does not bubble, so attach it to the nearest scrollable ancestor
    useEventListener(referenceElement ? getNearestOverflowAncestor(referenceElement) : document.body, "scroll", handleScroll);
    return {};
};
export const useEmitCloseEvent = ({ open, onOpenChange, refs }, { enabled, eventEmitId, trigger, }) => {
    const previousIsVisible = usePrevious(open);
    useEffect(() => {
        if (trigger === "modal")
            return;
        const handler = ({ id: emitterId, trigger: emittingTrigger }) => {
            var _a;
            const emittingTarget = document.querySelector(`[data-trigger-id='${emitterId}']`);
            if ((_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.contains(emittingTarget)) {
                return;
            }
            if (emittingTrigger === "hover" && trigger !== "hover") {
                return;
            }
            emitterId !== eventEmitId && onOpenChange(false);
        };
        portalEventEmitter.on("closeOthers", handler);
        return () => {
            portalEventEmitter.off("closeOthers", handler);
        };
    }, [eventEmitId, enabled]);
    useEffect(() => {
        const isToggledVisible = open && !previousIsVisible;
        if (enabled && isToggledVisible) {
            portalEventEmitter.emit("closeOthers", { id: eventEmitId, trigger });
        }
    }, [eventEmitId, open, enabled]);
    return {};
};
export const useShowOnTargetEnterSpace = ({ open }, { enabled, show }) => {
    if (!enabled) {
        return {};
    }
    return {
        reference: {
            onKeyDown: (e) => {
                if (!open && (e.key === "Enter" || e.key === " ")) {
                    show(0);
                }
            },
        },
    };
};
export const useFocusPortalChildOnTargetTab = ({ open, refs: { floating } }, { enabled, hide }) => {
    if (!enabled) {
        return {};
    }
    return {
        reference: {
            onKeyDown: (e) => {
                if (open && e.key === "Tab" && floating.current) {
                    const focusableChildren = getFocusableChildren(floating.current);
                    if (e.shiftKey || focusableChildren.length === 0) {
                        hide(0);
                        return;
                    }
                    trapEvent(e);
                    focusableChildren[0].focus();
                }
            },
        },
    };
};
export const useHideOnEscape = ({ open }, { enabled, hide, returnFocusOnHide }) => {
    if (!enabled) {
        return {};
    }
    return {
        reference: {
            onKeyDown: (e) => {
                if (open && e.key === "Escape") {
                    hide(0);
                    trapEvent(e);
                }
            },
        },
        floating: {
            onKeyDown: (e) => {
                if (open && e.key === "Escape") {
                    hide(0, returnFocusOnHide);
                    trapEvent(e);
                }
            },
        },
    };
};
export const useHideOnTargetBlur = ({ open, refs: { floating } }, { enabled, hide }) => {
    if (!enabled) {
        return {};
    }
    return {
        reference: {
            onBlur: (e) => {
                var _a;
                open && !((_a = floating.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget)) && hide(0);
            },
        },
    };
};
export const useHideOnPortalTabLastElement = ({ refs: { floating, reference } }, { enabled, hide }) => {
    if (!enabled) {
        return {};
    }
    return {
        floating: {
            onKeyDown: (e) => {
                e.stopPropagation();
                if (e.key !== "Tab" || !floating.current) {
                    return;
                }
                const { focusableChildren, focusedIndex } = getFocusedIndex(floating.current);
                const isLastFocusableAndTab = focusedIndex === focusableChildren.length - 1 && !e.shiftKey;
                const isFirstFocusableAndShiftTab = focusedIndex === 0 && e.shiftKey;
                if (isLastFocusableAndTab || isFirstFocusableAndShiftTab) {
                    e.stopPropagation();
                    const referenceElement = reference.current;
                    referenceElement &&
                        "focus" in referenceElement &&
                        typeof referenceElement.focus === "function" &&
                        referenceElement.focus();
                    hide(0, false);
                }
            },
        },
    };
};
export const useHideOnPortalTab = ({ open }, { enabled, hide, returnFocusOnHide }) => {
    if (!enabled) {
        return {};
    }
    return {
        floating: {
            onKeyDown: (e) => {
                if (open && e.key === "Tab") {
                    hide(0, returnFocusOnHide);
                }
            },
        },
    };
};
export const useStopPortalFocusPropagation = () => {
    {
        return {
            floating: {
                onFocus: (e) => {
                    e.stopPropagation();
                },
            },
        };
    }
};
export const useShowOnFocus = ({ open, refs }, { enabled, show, hide }) => {
    if (!enabled) {
        return {};
    }
    return {
        reference: {
            onFocus: () => {
                !open && show(0);
            },
        },
        floating: {
            onBlur: (e) => {
                var _a;
                open && ((_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget)) && hide(0, false);
            },
        },
    };
};
export const useHover = ({ open, refs: { reference } }, { enabled, show, hide }) => {
    if (!enabled) {
        return {};
    }
    return {
        reference: {
            onMouseEnter: () => show(),
            onMouseLeave: () => hide(),
            onPointerEnter: () => show(),
            onPointerLeave: () => hide(),
            onFocus: () => {
                const target = isDomElement(reference.current) ? reference.current : null;
                if (target === null || target === void 0 ? void 0 : target.hasAttribute("data-portal-open")) {
                    return;
                }
                !open && show(0);
            },
        },
        floating: {
            onMouseEnter: () => show(),
            onMouseLeave: () => hide(),
        },
    };
};
//# sourceMappingURL=interactions.js.map